<template>
  <svg :id="id" ref="svgElement" class="radar-chart simcision-chart">
    <defs>
      <radar-chart-patterns :id="id" :u="u"></radar-chart-patterns>
    </defs>
  </svg>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, reactive } from "vue";
import { v4 } from "uuid";
import { scaleLinear } from "d3-scale";
import { select, local } from "d3-selection";
import { line, arc } from "d3-shape";
import { interpolate } from "d3-interpolate";
import { axisRight } from "d3-axis";
import { easeExpOut } from "d3-ease";
import { useI18n } from "vue-i18n";

import { selectOrCreateChild, wrapText } from "./chartDrawFunction";
import { useChartEngine } from "./useChartEngine";
import { mapNumber } from "@/helpers/utility/MathHelper";

import RadarChartPatterns from "./RadarChartPatterns.vue";

import type { ScaleLinear } from "d3-scale";
import type { ChartComponentProps, ChartEmitEvents } from "./useChartEngine";
import type { DataEntry, EntryValue } from "./ChartData";

const { t } = useI18n();

const props = withDefaults(defineProps<ChartComponentProps>(), {
  id: () => v4(),
  yUnit: "%",
  width: 960,
  height: 500,
  settings: () => ({}),
  formattedTitle: () => [],
  animationsDisabled: false,
  titleClickable: false,
  margin: () => ({ top: 20, left: 20, bottom: 20, right: 20 })
});

const emit = defineEmits<ChartEmitEvents>();

// SETUP
const ctx = useChartEngine(() => props, { emit });
const chart = reactive(ctx);
const {
  animationDuration,
  domain,
  innerHeight,
  innerWidth,
  longestValuesArray,
  filteredData,
  titleHeight,
  svg,
  container,
  svgElement,
  labelsHeight,
  measureTextWidthCached,
  drawTitle,
  updateHoverBox,
  closeHoverBox
} = ctx;

const yScale = ref<ScaleLinear<number, number, never>>();
const lastAxisCount = ref(0);
const oldOuterRadii = ref(local<number>());
const oldInnerRadii = ref(local<number>());
const oldValueRadii = ref(local<number>());

const minDimension = computed(() => {
  return Math.min(innerHeight.value, innerWidth.value);
});
const chartRadius = computed(() => {
  return minDimension.value / 2;
});
const axisCount = computed(() => {
  return longestValuesArray.value.length;
});
const tickAmount = computed(() => {
  return Math.min(
    Math.max(Math.floor(mapNumber(chartRadius.value, 0, 400, 3, 10)), 3),
    10
  );
});
const longestLabelWidth = computed(() => {
  return longestValuesArray.value.reduce((longest, d) => {
    const t = props.settings.xAxisLabelsShortened ? d.shortTitle : d.title;

    // save the longest width
    const width = measureTextWidthCached(t, props.settings.fontSize || 12);
    return Math.max(longest, width);
  }, 0);
});

/**
 * Base unit for drawing the chart patterns
 * related to the current chart size.
 */
const u = computed(() => {
  return minDimension.value / 600;
});

// WATCHERS

watch([() => props.width, () => props.height], ([width, height]) => {
  if (!svg.value) return;
  svg.value.attr("width", width);
  svg.value.attr("height", height);
  svg.value
    .select(".chart__background")
    .attr("width", width)
    .attr("height", height);

  updateChart();
});

watch(
  [() => props.data, () => props.settings],
  () => {
    updateChart();
  },
  { deep: true, immediate: true }
);

// SETUP
// start updates

onMounted(() => {
  createChart();

  if (!props.animationsDisabled) {
    setTimeout(() => {
      animationDuration.value = 500;
    }, 200);
  }
});

/**
 * Setup the chart.
 * This needs to be called before any update functions.
 */
function createChart() {
  if (!svgElement.value) return;

  // Connect the SVG
  svg.value = select<SVGSVGElement, unknown>(svgElement.value)
    .attr("width", props.width)
    .attr("height", props.height);

  // Create the background rect
  // This is relevant for SVG exports.
  if (props.settings?.backgroundFilled !== false) {
    svg.value
      .append("rect")
      .attr("width", props.width)
      .attr("height", props.height)
      .attr("class", "chart__background");
  }

  // (Re-)Create the container
  const g = svg.value.append("g").attr("class", "chart__container");

  const header = svg.value
    .append("g")
    .attr("class", "chart__header");
  header.append("rect").attr("class", "chart__header-bg");
  header.append("text").attr("class", "chart__title");

  // create the axis containers
  g.append("g").attr("class", "radar-chart__areas");
  g.append("g").attr("class", "gx");
  g.append("g").attr("class", "gy");
  g.append("g").attr("class", "radar-chart__labels");

  // save the svg and container to the component data.
  container.value = g;

  updateChart();
}

/**
 * Update the SVG: change it to represent the
 * current state of the data array.
 */
function updateChart() {
  chart.labelsHeight = (props.settings.fontSize || 12) * 5.85;
  if (!container.value) return;

  positionContainer();
  updateScales();

  drawTitle();

  drawBars();
  drawAxes();
  drawTicks();
  drawLabels();
  drawScale();

  drawYAxisLabel();
}

/**
 * Position the container in the center of the chart.
 */
function positionContainer() {
  const g = container.value;
  if (!g) return;

  // update labels width
  if (!props.settings.xAxisLabelsVisible) {
    chart.labelsWidth = 0;
  } else {
    chart.labelsWidth = Math.min(longestLabelWidth.value, 200);
  }

  chart.paddingLeft = chart.labelsWidth + 10;
  chart.paddingRight = chart.labelsWidth + 10;

  g.attr(
    "transform",
    `translate(${props.margin.left + chart.paddingLeft}, ${
      props.margin.top + labelsHeight.value / 2 + titleHeight.value
    })`
  );
}

/**
 * Update xScale and yScale, depending on the data and the chart dimensions.
 */
function updateScales() {
  yScale.value = scaleLinear()
    .domain(domain.value)
    .nice(10)
    .range([0, chartRadius.value]);
}

/**
 * Draw two lines from the center outwards for every axis of the chart,
 * bordering the axis area.
 */
function drawAxes() {
  if (container.value == null) return;
  if (axisCount.value == 0) return;
  if (yScale.value == null) return;

  if (!props.settings.ticksVisible) {
    container.value.selectAll(".radar-chart__axis").remove();
    return;
  }

  const domain = yScale.value.domain();

  container.value
    .select(".gy")
    .attr(
      "transform",
      `translate(${innerWidth.value / 2}, ${innerHeight.value / 2})`
    )
    .selectAll<SVGPathElement, EntryValue>(".radar-chart__axis")
    .data(longestValuesArray.value, (d) => d.key)
    .join("path")
    .attr("class", "radar-chart__axis")
    .transition()
    .duration(animationDuration.value)
    .attr("d", (d, i) => {
      const a: number = i - 0.5;
      return line()([
        [getCoordX(a, domain[0]), getCoordY(a, domain[0])],
        [getCoordX(a, domain[1]), getCoordY(a, domain[1])]
      ]);
    });
}

/**
 * Draw the circle ticks.
 */
function drawTicks() {
  if (container.value == null) return;
  if (axisCount.value == 0) return;
  if (yScale.value == null) return;

  const scale = yScale.value;

  if (!props.settings.ticksVisible) {
    container.value.selectAll(".radar-chart__tick").remove();
    return;
  }

  const oldTickCount = container.value.selectAll(".radar-chart__tick").size();

  const ANIMATION_DURATION = oldTickCount == 0 ? 0 : animationDuration.value;

  const ticks = scale.ticks(tickAmount.value);
  const domainMax = scale.domain()[1];

  container.value
    .select(".gx")
    .attr(
      "transform",
      `translate(${innerWidth.value / 2}, ${innerHeight.value / 2})`
    )
    .selectAll<SVGElement, number>(".radar-chart__tick")
    .data(ticks, (d) => d)
    .join(
      (enter) =>
        enter
          .append("circle")
          .attr("class", "radar-chart__tick")
          .attr("r", (d) => Math.min(Math.max(scale(d), 0), scale(domainMax)))
          .style("opacity", 0)
          .attr("cx", 0)
          .attr("cy", 0),
      (update) => update,
      (exit) =>
        exit
          .transition()
          .duration(ANIMATION_DURATION)
          .attr("r", (d) => Math.min(Math.max(scale(d), 0), scale(domainMax)))
          .style("opacity", 0)
          .remove()
    )
    .transition()
    .duration(ANIMATION_DURATION)
    .attr("r", (d) => Math.max(0, scale(d)))
    .style("opacity", 0.2);
}

/**
 * Draw the bars
 */
function drawBars() {
  if (container.value == null) return;
  if (axisCount.value == 0) return;
  if (yScale.value == null) return;
  const scale = yScale.value;
  const data = filteredData.value;

  const groups = container.value
    .select(".radar-chart__areas")
    .selectAll<SVGElement, DataEntry>(".radar-chart__group")
    .data(data, (d) => d.key)
    .join("g")
    .attr("class", "radar-chart__group")
    .attr("data-key", (d) => d.key)
    .attr(
      "transform",
      `translate(${innerWidth.value / 2}, ${innerHeight.value / 2})`
    );

  const sliceRadians = (Math.PI * 2) / axisCount.value;
  const sliceDegrees = 360 / axisCount.value;

  const arcGenerator = arc();

  const ANGLE_PAD = 0.002;
  const ANIMATION_DURATION = animationDuration.value;

  const domainMax = scale.domain()[1];

  const previousData = local<{ d: EntryValue; i: number }>();

  const MAIN_BAR_COLOR = "hsl(207, 38%, 34%)";

  // create bars
  groups
    .selectAll<SVGGElement, EntryValue>(".radar-chart__bar")
    .each(function (d, i) {
      previousData.set(this, { d, i });
    })
    .data(
      (d) => d.values,
      (c) => (c as { key: string }).key
    )
    .join(
      (enter) => {
        const barGroup = enter
          .append("g")
          .attr("class", "radar-chart__bar")
          .attr("data-key", (d) => d.key)
          .attr(
            "transform",
            (d, i) => `rotate(${(i + 0.5) * sliceDegrees - 180})`
          )
          .on("mousemove", (event, v) => {
            const x = event.clientX;
            const y = event.clientY;

            updateHoverBox({ x, y }, v);
          })
          .on("mouseout", () => {
            closeHoverBox();
          });

        barGroup.each((d, index, groups) => {
          const groupElement = groups[index];
          const group = select<SVGGElement, EntryValue>(groupElement);

          const outer = Math.max(scale(d.value || 0), scale(d.startValue || 0));
          const inner = Math.min(scale(d.startValue || 0), scale(d.value || 0));
          const valueRadius = scale(d.value || 0);

          oldOuterRadii.value.set(groupElement, outer);
          oldInnerRadii.value.set(groupElement, inner);
          oldValueRadii.value.set(groupElement, valueRadius);

          // main bar
          group
            .append("path")
            .attr("class", "radar-chart__bar-main")
            .attr("fill", () =>
              props.settings.areasColored
                ? d.color || MAIN_BAR_COLOR
                : MAIN_BAR_COLOR
            )
            .transition()
            .duration(ANIMATION_DURATION)
            .attrTween("d", function () {
              const start = {
                innerRadius: 0,
                outerRadius: 0,
                startAngle: -ANGLE_PAD - sliceRadians / 2,
                endAngle: sliceRadians / 2
              };
              const end = {
                innerRadius: scale(0),
                outerRadius: outer,
                startAngle: -ANGLE_PAD - sliceRadians / 2,
                endAngle: sliceRadians / 2
              };
              const interpolateAngles = interpolate(start, end);
              return function (t: number) {
                return arcGenerator(interpolateAngles(t)) || "";
              };
            });

          // difference bar
          group
            .append("path")
            .attr("class", "radar-chart__bar-difference")
            .attr("fill", () =>
              (d.startValue || 0) < (d.value || 0)
                ? `url(#${props.id}-radar-chart__pattern-ascending${
                    props.settings.areasColored ? "" : "--colored"
                  })`
                : `url(#${props.id}-radar-chart__pattern-descending${
                    props.settings.areasColored ? "" : "--colored"
                  })`
            )
            .transition()
            .duration(ANIMATION_DURATION)
            .attrTween("d", function () {
              const start = {
                innerRadius: 0,
                outerRadius: 0,
                startAngle: -ANGLE_PAD - sliceRadians / 2,
                endAngle: sliceRadians / 2
              };
              const end = {
                innerRadius: inner,
                outerRadius: outer + 1,
                startAngle: -ANGLE_PAD - sliceRadians / 2,
                endAngle: sliceRadians / 2
              };
              const interpolateAngles = interpolate(start, end);
              return function (t: number) {
                return arcGenerator(interpolateAngles(t)) || "";
              };
            })
            .attr("fill", () =>
              (d.startValue || 0) < (d.value || 0)
                ? `url(#${props.id}-radar-chart__pattern-ascending${
                    props.settings.areasColored ? "" : "--colored"
                  })`
                : `url(#${props.id}-radar-chart__pattern-descending${
                    props.settings.areasColored ? "" : "--colored"
                  })`
            );

          group
            .append("path")
            .attr("class", "radar-chart__bar-valueline")
            .attr("fill", "var(--color-text)")
            .transition()
            .duration(ANIMATION_DURATION)
            .attrTween("d", function () {
              const start = {
                innerRadius: 0,
                outerRadius: 0,
                startAngle: -ANGLE_PAD - sliceRadians / 2,
                endAngle: sliceRadians / 2 + ANGLE_PAD
              };
              const end = {
                innerRadius: valueRadius - 1,
                outerRadius: valueRadius + 1,
                startAngle: -ANGLE_PAD - sliceRadians / 2,
                endAngle: sliceRadians / 2 + ANGLE_PAD
              };
              const interpolateAngles = interpolate(start, end);
              return function (t: number) {
                return arcGenerator(interpolateAngles(t)) || "";
              };
            });
        });

        return barGroup;
      },
      (update) => {
        return update
          .transition()
          .duration(animationDuration.value)
          .attr(
            "transform",
            (d, i) => `rotate(${(i + 0.5) * sliceDegrees - 180})`
          )
          .each((d, index, groups) => {
            const groupElement = groups[index];
            const group = select<SVGGElement, EntryValue>(groupElement);

            const outer = Math.max(scale(d.value || 0), scale(d.startValue || 0));
            const inner = Math.min(scale(d.startValue || 0), scale(d.value || 0));
            const valueRadius = scale(d.value || 0);

            const oldOuter = oldOuterRadii.value.get(groupElement) ?? outer;
            const oldInner = oldInnerRadii.value.get(groupElement) ?? inner;
            const oldValueRadius =
              oldValueRadii.value.get(groupElement) ?? valueRadius;
            const oldData = previousData.get(groupElement);

            const oldSliceRadians =
              lastAxisCount.value == 0
                ? 0
                : (Math.PI * 2) / lastAxisCount.value;

            group
              .select<SVGPathElement>(".radar-chart__bar-main")
              .transition()
              .duration(animationDuration.value)
              .attr("fill", () =>
                props.settings.areasColored
                  ? d.color || MAIN_BAR_COLOR
                  : MAIN_BAR_COLOR
              )
              .attrTween("d", function () {
                const oldD = oldData?.d;

                const start = {
                  innerRadius: scale(0),
                  outerRadius:
                    oldOuter ??
                    scale(Math.min(domainMax, oldD?.value || d.value || 0)),
                  startAngle: -ANGLE_PAD - oldSliceRadians / 2,
                  endAngle: oldSliceRadians / 2
                };
                const end = {
                  innerRadius: scale(0),
                  outerRadius: outer,
                  startAngle: -ANGLE_PAD - sliceRadians / 2,
                  endAngle: sliceRadians / 2
                };
                const interpolateAngles = interpolate(start, end);
                return function (t: number) {
                  return arcGenerator(interpolateAngles(t)) || "";
                };
              });

            // update difference bar
            group
              .select(".radar-chart__bar-difference")
              .transition()
              .delay(0)
              .duration(animationDuration.value)
              .attrTween("d", function () {
                const start = {
                  innerRadius: oldInner,
                  outerRadius: oldOuter + 1,
                  startAngle: -ANGLE_PAD - oldSliceRadians / 2,
                  endAngle: oldSliceRadians / 2
                };
                const end = {
                  innerRadius: inner,
                  outerRadius: outer + 1,
                  startAngle: -ANGLE_PAD - sliceRadians / 2,
                  endAngle: sliceRadians / 2
                };
                const interpolateAngles = interpolate(start, end);
                return function (t: number) {
                  return arcGenerator(interpolateAngles(t)) || "";
                };
              })
              .attr("fill", () =>
                (d.startValue || 0) < (d.value || 0)
                  ? `url(#${props.id}-radar-chart__pattern-ascending${
                      props.settings.areasColored ? "" : "--colored"
                    })`
                  : `url(#${props.id}-radar-chart__pattern-descending${
                      props.settings.areasColored ? "" : "--colored"
                    })`
              );

            group
              .select(".radar-chart__bar-valueline")
              .transition()
              .duration(animationDuration.value)
              .attrTween("d", function () {
                const start = {
                  innerRadius: oldValueRadius - 1,
                  outerRadius: oldValueRadius + 1,
                  startAngle: -ANGLE_PAD - oldSliceRadians / 2,
                  endAngle: oldSliceRadians / 2
                };
                const end = {
                  innerRadius: valueRadius - 1,
                  outerRadius: valueRadius + 1,
                  startAngle: -ANGLE_PAD - sliceRadians / 2,
                  endAngle: sliceRadians / 2 + ANGLE_PAD
                };
                const interpolateAngles = interpolate(start, end);
                return function (t: number) {
                  return arcGenerator(interpolateAngles(t)) || "";
                };
              });

            oldOuterRadii.value.set(groupElement, outer);
            oldInnerRadii.value.set(groupElement, inner);
            oldValueRadii.value.set(groupElement, valueRadius);
          });
      },
      (exit) => exit.remove()
    );

  lastAxisCount.value = axisCount.value;
}

/**
 * Draw the labels at the end of each axis area.
 */
function drawLabels() {
  if (container.value == null) return;
  if (axisCount.value == 0) return;
  if (yScale.value == null) return;

  if (!props.settings.xAxisLabelsVisible) {
    container.value.selectAll(".radar-chart__label").remove();
    return;
  }

  const fontSize = props.settings.fontSize || 12;
  const LABEL_Y = yScale.value.domain()[1];
  const LABELS_OFFSET = fontSize + 2;

  const labels = container.value
    .select(".radar-chart__labels")
    .attr(
      "transform",
      `translate(${innerWidth.value / 2}, ${innerHeight.value / 2})`
    )
    .selectAll(".radar-chart__label")
    .data(longestValuesArray.value, (d) => (d as { key: string }).key)
    .join(
      (enter) =>
        enter
          .append("text")
          .attr("class", "radar-chart__label")
          .attr("x", (d, i) => Math.round(getCoordX(i, LABEL_Y * 1.1)))
          .attr("y", (d, i) => Math.round(getCoordY(i, LABEL_Y * 1.1))),
      (update) => update,
      (exit) => exit.remove()
    )
    .text((d) => {
      return props.settings.xAxisLabelsShortened ? d.shortTitle : d.title;
    })
    .attr("text-anchor", (d, i) => {
      const x = getCoordX(i, LABEL_Y);
      if (x > 5) {
        return "start";
      } else if (x < -1) {
        return "end";
      } else {
        return "middle";
      }
    })
    .attr("text-anchor-vertical", (d, i) => {
      const y = getCoordY(i, LABEL_Y);
      if (y > 5) {
        return "start";
      } else if (y < -5) {
        return "end";
      } else {
        return "middle";
      }
    })
    .on("dblclick", (_event, d) => {
      emit("click:column-title", {
        uid: d.uid,
        title: d.title,
        shortTitle: d.shortTitle,
        type: d.type,
        id: d.id
      });
    });

  const labelsTransition = labels
    .transition()
    .duration(animationDuration.value)
    .ease(easeExpOut)
    .attr("x", (d, i) => Math.round(getCoordX(i, LABEL_Y, LABELS_OFFSET)))
    .attr("y", (d, i) => Math.round(getCoordY(i, LABEL_Y, LABELS_OFFSET)) + 4)
    .attr("font-size", fontSize);

  wrapText(
    labelsTransition,
    (d, i) => {
      const x = Math.abs(Math.round(getCoordX(i, LABEL_Y, LABELS_OFFSET)));
      return innerWidth.value / 2 + chart.labelsWidth - x;
    },
    measureTextWidthCached,
    fontSize
  );

  labels
    .selectAll("title")
    .data((d) => [d])
    .join("title")
    .text((d) => d.title);
}

/**
 * Draw a line with all tick values of the scale
 * E.g. "0, 10, 20, 30, 40, 50, 60, 70, 80, 90"
 */
function drawScale() {
  if (container.value == null) return;
  if (axisCount.value == 0) return;
  if (yScale.value == null) return;

  const g = container.value.select(".gy");

  if (!props.settings.yAxisLabelsVisible) {
    g.select(".chart__y-ticks").remove();
    return;
  }

  const drawAxis = axisRight(yScale.value).ticks(tickAmount.value);

  if (g.select(".chart__y-ticks").empty()) {
    g.append("g").attr("class", "chart__y-ticks");
  }

  g.select<SVGGElement>(".chart__y-ticks")
    .classed("-overlaying", true)
    .transition()
    .duration(animationDuration.value)
    .call(drawAxis);
  
  const fontSize = props.settings.fontSize || 12;
  g.selectAll<SVGTextElement, unknown>(
    ".chart__y-ticks .tick text"
  ).attr("font-size", fontSize);
}

/**
 * Draw the y axis label:
 * "Status"
 */
function drawYAxisLabel() {
  const g = container.value;
  if (!g) return;

  if (!props.settings.yAxisTitleVisible) {
    g.select(".axis-label--y").remove();
    return;
  }

  const fontSize = props.settings.fontSize || 12;

  // draw axis label
  const yAxisLabel = selectOrCreateChild(g, "g.axis-label--y")
    .attr(
      "transform",
      `translate(${innerWidth.value / 2 - fontSize * 1.4}, ${innerHeight.value / 2})`
    )
    .on("click", (event) => {
      emit("click:axis-title", {
        axis: "y",
        x: event.clientX,
        y: event.clientY
      });
    });

  const text = props.settings.yAxis
    ? t(`chartAxes.${props.settings.yAxis}`)
    : "";

  // shadow
  selectOrCreateChild(yAxisLabel, "text.axis-label__shadow")
    .attr("x", -10)
    .attr("y", 2)
    .attr("dy", "1em")
    .attr("text-anchor", "end")
    .attr("transform", "rotate(-90)")
    .attr("font-size", fontSize)
    .attr("stroke", "var(--color-background-deep)")
    .attr("stroke-width", 4)
    .attr("stroke-linejoin", "round")
    .attr("stroke-linecap", "round")
    .style("opacity", 0.7)
    .text(text);

  // text
  selectOrCreateChild(yAxisLabel, "text.axis-label__text")
    .attr("x", -10)
    .attr("y", 2)
    .attr("dy", "1em")
    .attr("text-anchor", "end")
    .attr("transform", "rotate(-90)")
    .attr("font-size", fontSize + "px")
    .attr("fill", "var(--color-text)")
    .text(text);
}

/**
 * Get the x coordinate of a value on a given axis.
 * @param axis Index of the radar chart axis (0 is bottom, moving clockwise)
 * @param value Value to interpolate
 * @param offset Offset from the center of the chart
 */
function getCoordX(axis: number, value: number, offset = 0) {
  const scale = yScale.value;
  if (scale == null) return 0;
  const angle = ((axis + 0.5) / axisCount.value) * 360 - 90;
  return -Math.cos((angle * Math.PI) / 180) * (scale(value) + offset);
}

/**
 * Get the y coordinate of a value on a given axis.
 * @param axis Index of the radar chart axis (0 is bottom, moving clockwise)
 * @param value Value to interpolate
 * @param offset Offset from the center of the chart
 */
function getCoordY(axis: number, value: number, offset = 0) {
  const scale = yScale.value;
  if (scale == null) return 0;
  const angle = ((axis + 0.5) / axisCount.value) * 360 - 90;
  return -Math.sin((angle * Math.PI) / 180) * (scale(value) + offset);
}
</script>

<style lang="scss">
@use "./chart.scss";

.radar-chart {
  .chart__y-ticks .tick:nth-child(2) {
    opacity: 0;
  }
  .chart__y-ticks {
    pointer-events: none;
  }

  &__tick {
    fill: none;
    stroke: #aaa;
    stroke-width: 2;
    opacity: 0.2;
    mix-blend-mode: exclusion;
    pointer-events: none;
  }
  &__axis {
    fill: none;
    stroke: #aaa;
    opacity: 0.3;
    stroke-width: 1;
    mix-blend-mode: exclusion;
    pointer-events: none;
  }
  &__group {
    opacity: 1;
  }
  &__label {
    fill: var(--color-text);
    font-family: var(--font-family-ui);

    // .-small & {
    //   font-size: 10px;
    // }
  }
}
</style>
